import { Button, Flex, Typography } from "antd";
import {
  ArrowRightOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import React from "react";
import { t } from "i18next";

import {
  FacturaAprobadaIcon,
  FacturaPendienteIcon,
  PagoPendienteIcon,
  PagoVerificadoIcon,
} from "../icon/PagoPendienteIcon";
import {
  EstadoPaso,
  FlujoPedido,
  PasoPedido,
  useEsperaDelAvance,
} from "../order-flow/flujo-pedido";

type CreditStepsProps = {
  currentStep: number;
  memo: any;
  canSendReview: boolean;
  canSendInvoice: boolean;
  handleChangeStep: (status: string) => void;
  loadingStep: boolean;
  delivery?: any;
  shipmentInfo?: any;
  /** Las entregas que canceló la nota: todas, en una cancelación total del pedido. */
  entregas?: { id?: number; name?: string }[];
  /**
   * Cancelación total del pedido: el flujo empieza con ese nombre en vez de listar las
   * entregas, que suelen llamarse igual (Diseño, 2026-10-19).
   */
  cancelacionTotal?: boolean;
};

const { Text } = Typography;

/**
 * Flujo de una nota de crédito: el mismo de la entrega y del pago (`FlujoPedido`), con la
 * entrega cancelada al principio y el botón de cambio de estado sobre la línea.
 */
export const CreditSteps: React.FC<CreditStepsProps> = ({
  currentStep,
  memo,
  canSendReview,
  handleChangeStep,
  loadingStep = false,
  shipmentInfo,
  entregas: entregasProp,
  cancelacionTotal = false,
}) => {
  const { esperando, esperar } = useEsperaDelAvance(currentStep);

  // Las entregas canceladas abren el flujo, con el mismo estilo que las entregas del flujo
  // del pago: camión, verde y en negrita, y cada una lleva a su panel. Una cancelación total
  // del pedido las trae todas; si no vino la lista, la de `shipmentInfo`.
  const entregas: { id?: number; name?: string }[] = cancelacionTotal
    ? [
        {
          id: entregasProp?.[0]?.id ?? shipmentInfo?.id,
          name: t("orders.label.creditnoteBulkFullCancellation"),
        },
      ]
    : entregasProp?.length
      ? entregasProp
      : [{ id: shipmentInfo?.id, name: shipmentInfo?.name }];

  const inicio = (
    <div className="flujo-pedido__lista">
      {entregas.map((entrega, i) => (
        <Flex
          key={entrega.id ?? i}
          align="flex-start"
          gap={6}
          className="flujo-pedido__entrega"
        >
          <TruckOutlined style={{ fontSize: 20 }} />
          {entrega.id ? (
            <Button
              type="link"
              className="flujo-pedido__entrega-nombre"
              title={entrega.name}
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("openShipmentPanel", {
                    detail: { shipmentId: entrega.id },
                  }),
                );
                setTimeout(() => {
                  document
                    .getElementById(`shipment-panel-${entrega.id}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 350);
              }}
            >
              <Text type="secondary">{entrega.name}</Text>
            </Button>
          ) : (
            <Text type="secondary">
              {entrega.name ?? t("orders.label.deliveryCancelled")}
            </Text>
          )}
        </Flex>
      ))}
    </div>
  );

  // Pago verificado queda hecho al llegar: no tiene nada más que hacer.
  const estado = (i: number, ultimo = false): EstadoPaso =>
    currentStep > i || (ultimo && currentStep >= i)
      ? "hecho"
      : currentStep === i
        ? "actual"
        : "pendiente";

  const pasos: PasoPedido[] = [
    {
      key: "pending",
      icon: <FacturaPendienteIcon />,
      label: t("orders.label.pending.invoice"),
      estado: estado(0),
    },
    {
      key: "approved",
      icon: <FacturaAprobadaIcon />,
      label: t("orders.label.invoiceapproved"),
      estado: estado(1),
    },
    {
      key: "pending_payment",
      icon: <PagoPendienteIcon />,
      label: t("orders.steps.pendingPayment"),
      estado: estado(2),
    },
    {
      key: "payment_verified",
      icon: <PagoVerificadoIcon />,
      label: t("orders.label.verifiedPayment"),
      estado: estado(3, true),
    },
  ];

  const botonAvanzar = (
    texto: string,
    siguiente: string,
    disabled = false,
  ) => (
    <Button
      loading={loadingStep || esperando}
      type="primary"
      disabled={disabled}
      iconPosition="end"
      icon={<ArrowRightOutlined />}
      onClick={() => {
        esperar();
        handleChangeStep(siguiente);
      }}
    >
      {texto}
    </Button>
  );

  const boton =
    currentStep === 0
      ? {
          despuesDe: "pending",
          nodo: botonAvanzar(
            t("orders.steps.invoiceAndSend"),
            "approved",
            !canSendReview,
          ),
        }
      : currentStep === 1
        ? {
            despuesDe: "approved",
            nodo: botonAvanzar(t("button.sendtoreview"), "pending_payment"),
          }
        : currentStep === 2
          ? {
              despuesDe: "pending_payment",
              nodo: botonAvanzar(
                t("orders.steps.confirmPayment"),
                "payment_verified",
              ),
            }
          : undefined;

  return (
    <FlujoPedido
      id={memo?.creditMemoId ? `nota-${memo.creditMemoId}` : undefined}
      inicio={inicio}
      pasos={pasos}
      boton={boton}
    />
  );
};
